import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, ChevronRight, BookOpen } from "lucide-react";
import { useApp } from "../context/AppContext";
import { PATHWAYS } from "../data/mockData";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { RADIO_HERO_PORTRAIT } from "../lib/brandAssets";

export default function Home() {
  const { playTrack } = useApp();
  const [featured, setFeatured] = useState({});
  const [teachings, setTeachings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [radioCfg, setRadioCfg] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [f, p, c, e, r, s] = await Promise.all([
          api.get("/content/featured"),
          api.get("/content/podcast/episodes").catch(() => ({ data: [] })),
          api.get("/content/courses").catch(() => ({ data: [] })),
          api.get("/content/events"),
          api.get("/content/radio-config"),
          api.get("/content/settings"),
        ]);
        setFeatured(f.data || {});
        setTeachings((p.data || []).slice(0, 4));
        setCourses((c.data || []).slice(0, 2));
        setNextEvent((e.data && e.data[0]) || null);
        setRadioCfg(r.data);
        setSettings(s.data || {});
      } catch {}
    })();
  }, []);

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null);

  const heroReflection = featured.hero_reflection;
  const tagline = settings.tagline || "Awaken. Re-Member. Return.";
  const motto = settings.motto || "Love is the doctrine. Compassion is the practice. Awakening is the work.";

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bloom" />
        <div className="grain absolute inset-0" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7 rise">
            <div className="eyebrow mb-5">{settings.temple_name || "The Temple of the Human Heart"}</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={{ color: "var(--burgundy)" }}>
              {tagline.split(/[.·]/).filter(Boolean).map((word, i) => (
                <span key={i}>
                  <span style={{ color: i === 0 ? "var(--burgundy)" : i === 1 ? "var(--rose-dark)" : "var(--gold-dark)", fontStyle: i === 2 ? "italic" : "normal" }}>
                    {word.trim()}.
                  </span>
                  {i < 2 ? " " : ""}
                </span>
              ))}
            </h1>
            <p className="mt-6 text-lg leading-relaxed max-w-xl" style={{ color: "var(--text-obsidian)" }}>
              A contemplative sanctuary for the interior life — a way of listening that returns you home to the presence that has always been within you.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {heroReflection && (
                <button data-testid="hero-play-reflection-btn"
                  onClick={() => { playTrack({ ...heroReflection, art: heroReflection.art_url }); toast(`Playing "${heroReflection.title}"`); }}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium"
                  style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
                  <Play className="w-4 h-4" /> {heroReflection.title}{heroReflection.duration ? ` · ${heroReflection.duration}` : ""}
                </button>
              )}
              <Link to="/join" data-testid="hero-join-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium border"
                style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>
                Enter Communion <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <blockquote className="mt-10 pl-4 border-l-2 font-serif italic text-lg max-w-md" style={{ borderColor: "var(--gold)", color: "var(--text-muted)" }}>
              "{motto}"
            </blockquote>
          </div>
          <div className="lg:col-span-5 rise" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl" style={{ background: "linear-gradient(135deg, var(--gold-soft), var(--rose))", opacity: 0.35 }} />
              <img
                src={RADIO_HERO_PORTRAIT}
                alt="Rev. Yisra'el Apriale — Temple of the Human Heart Radio"
                data-testid="home-radio-hero-img"
                className="relative rounded-2xl w-full h-[420px] sm:h-[520px] object-cover object-top"
              />
              <div className="absolute bottom-4 left-4 right-4 rounded-xl px-4 py-3 backdrop-blur-md flex items-center gap-3" style={{ background: "rgba(84,28,43,0.72)", color: "var(--bg-cream)" }}>
                <div className="waveform text-[color:var(--gold)]"><span/><span/><span/><span/><span/></div>
                <div className="text-xs">
                  <div className="opacity-80">Temple Radio</div>
                  <div className="font-serif text-base leading-tight">{radioCfg?.station_name || "Coming soon"}</div>
                </div>
                <Link to="/listen" data-testid="hero-listen-live-btn"
                  className="ml-auto rounded-full px-3 py-1.5 text-xs font-medium"
                  style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
                  {radioCfg?.listen_live_enabled ? "Listen Live" : "Visit Radio"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
          <div>
            <div className="eyebrow mb-2">Five Pathways</div>
            <h2 className="font-serif text-3xl sm:text-4xl" style={{ color: "var(--burgundy)" }}>Choose a door.</h2>
          </div>
          <p className="text-sm max-w-md" style={{ color: "var(--text-muted)" }}>
            Every practice begins with a small choice. There is no wrong door — each opens into the same interior chamber.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {PATHWAYS.map((p, i) => (
            <Link key={p.slug} to={`/${p.slug}`} data-testid={`pathway-${p.slug}-card`}
              className="lift rounded-2xl p-6 border relative overflow-hidden rise"
              style={{ background: i === 1 ? "var(--burgundy)" : "var(--bg-cream-card)", color: i === 1 ? "var(--bg-cream)" : "var(--text-obsidian)", borderColor: i === 1 ? "var(--burgundy)" : "var(--border-cream)", animationDelay: `${i * 0.05}s` }}>
              <div className="text-6xl font-serif opacity-15 absolute top-2 right-3 leading-none">0{i + 1}</div>
              <div className="font-mono text-[10px] tracking-widest uppercase mb-4" style={{ color: i === 1 ? "var(--gold-soft)" : "var(--gold-dark)" }}>{p.verse}</div>
              <div className="font-serif text-2xl mb-2">{p.label}</div>
              <p className="text-sm leading-relaxed opacity-90">{p.body}</p>
              <div className="mt-6 inline-flex items-center gap-1 text-xs font-medium">
                Enter <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="home-learning-circle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
            <div>
              <div className="eyebrow mb-2">The Learning Circle</div>
              <h2 className="font-serif text-3xl sm:text-4xl" style={{ color: "var(--burgundy)" }}>Enter the work.</h2>
            </div>
            <Link to="/learn" data-testid="learn-view-all-link" className="text-sm font-medium ink-link" style={{ color: "var(--burgundy)" }}>
              Explore the Learning Circle →
            </Link>
          </div>
          {courses.length === 0 ? (
            <div className="rounded-2xl border p-8 text-center" style={{ background: "var(--bg-cream)", borderColor: "var(--border-cream)" }}>
              <p className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>The first course is being prepared.</p>
            </div>
          ) : (
            <div className={`grid gap-6 ${courses.length > 1 ? "md:grid-cols-2" : "md:grid-cols-5"}`}>
              {courses.map((c) => (
                <Link key={c.id} to={`/learn/${c.id}`} data-testid={`home-course-${c.id}-card`}
                  className={`lift rounded-3xl border overflow-hidden grid ${courses.length > 1 ? "" : "md:col-span-5 md:grid-cols-5"}`}
                  style={{ background: "var(--burgundy)", color: "var(--bg-cream)", borderColor: "var(--burgundy)" }}>
                  {c.art_url && <div className={`${courses.length > 1 ? "h-44" : "md:col-span-2 h-56 md:h-full"} min-h-[180px]`}><img src={assetUrl(c.art_url)} alt="" className="w-full h-full object-cover" /></div>}
                  <div className={`p-7 sm:p-8 flex flex-col ${courses.length > 1 || !c.art_url ? "" : "md:col-span-3"}`}>
                    <div className="eyebrow mb-3 flex items-center gap-2" style={{ color: "var(--gold-soft)" }}><BookOpen className="w-3.5 h-3.5" /> Course{c.lesson_count ? ` · ${c.lesson_count} Encounter${c.lesson_count === 1 ? "" : "s"}` : ""}</div>
                    <h3 className="font-serif text-2xl sm:text-3xl leading-tight">{c.title}</h3>
                    {c.subtitle && <p className="text-sm mt-2 opacity-85">{c.subtitle}</p>}
                    {c.description && <p className="text-sm mt-3 leading-relaxed opacity-85 line-clamp-3">{c.description}</p>}
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--gold)" }}>Begin the course <ArrowRight className="w-4 h-4" /></div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="eyebrow mb-2">From the Podcast</div>
              <h2 className="font-serif text-2xl sm:text-3xl" style={{ color: "var(--burgundy)" }}>Recent teachings.</h2>
            </div>
            <Link to="/listen" data-testid="listen-all-link" className="text-sm font-medium ink-link" style={{ color: "var(--burgundy)" }}>All episodes →</Link>
          </div>
          {teachings.length === 0 ? (
            <div className="rounded-2xl border p-8 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
              <p className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>New episodes are on their way.</p>
              <Link to="/listen" className="ink-link text-sm" style={{ color: "var(--burgundy)" }}>Visit Listen →</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {teachings.map((p) => (
                <div key={p.guid} data-testid={`home-podcast-${p.guid}-card`}
                  className="lift rounded-2xl border p-5 flex flex-col min-w-0 overflow-hidden"
                  style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
                  <div className="eyebrow mb-2 whitespace-normal break-words">{[p.episode ? `Episode ${p.episode}` : null, fmtDate(p.published_at), p.duration].filter(Boolean).join(" · ")}</div>
                  <Link to={`/listen?episode=${encodeURIComponent(p.guid)}`} data-testid={`home-podcast-${p.guid}-link`} className="font-serif text-lg leading-tight ink-link break-words" style={{ color: "var(--burgundy)" }}>{p.title}</Link>
                  {p.description && <p className="text-xs mt-2 leading-relaxed line-clamp-3" style={{ color: "var(--text-muted)" }}>{p.description}</p>}
                  <div className="mt-auto pt-4 flex items-center gap-3">
                    <button data-testid={`home-podcast-${p.guid}-play-btn`} onClick={() => { playTrack({ id: p.guid, kind: "podcast", title: p.title, host: p.host, duration: p.duration, audio_url: p.audio_url, art: p.art_url }); toast(`Playing "${p.title}"`); }}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
                      <Play className="w-3 h-3" /> Play
                    </button>
                    <Link to={`/listen?episode=${encodeURIComponent(p.guid)}`} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Open in Podcast →</Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl p-6 relative overflow-hidden" style={{ background: "var(--obsidian)", color: "var(--bg-cream)" }}>
          <div className="grain absolute inset-0" />
          <div className="relative">
            <div className="eyebrow mb-2" style={{ color: "var(--gold-soft)" }}>Next Gathering</div>
            {nextEvent ? (
              <>
                <div className="font-serif text-2xl mb-2" style={{ color: "var(--gold)" }}>{nextEvent.title}</div>
                <div className="text-sm opacity-85 mb-6">{nextEvent.date}{(nextEvent.time || nextEvent.start_time) ? ` · ${nextEvent.time || nextEvent.start_time}` : ""} · {nextEvent.format}</div>
                {nextEvent.description && <p className="text-sm opacity-85 mb-6 leading-relaxed line-clamp-4">{nextEvent.description}</p>}
                <Link to={`/gather/${nextEvent.id}`} data-testid="home-next-event-link"
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
                  style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
                  {nextEvent.registration_mode === "none" ? "View gathering" : "Reserve a seat"} <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <div data-testid="home-next-event-empty">
                <div className="font-serif text-xl" style={{ color: "var(--gold)" }}>No gathering is scheduled right now.</div>
                <p className="text-sm opacity-85 mt-2 leading-relaxed">When the next Temple gathering is announced, it will appear here. Until then, Temple Radio and the podcast are always open.</p>
                <Link to="/gather" data-testid="home-gather-link" className="inline-flex items-center gap-2 mt-5 text-sm font-medium" style={{ color: "var(--gold)" }}>Visit Gather <ArrowRight className="w-4 h-4" /></Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto rounded-3xl overflow-hidden relative" style={{ background: "var(--surface-deep)" }}>
          <div className="grain absolute inset-0" />
          <div className="relative p-8 sm:p-14 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="eyebrow mb-3" style={{ color: "var(--gold-soft)" }}>An invitation</div>
              <h3 className="font-serif text-3xl sm:text-4xl leading-tight" style={{ color: "var(--bg-cream)" }}>
                The door is already open. Cross the threshold.
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:justify-end">
              <Link to="/join" data-testid="cta-join-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium"
                style={{ background: "var(--gold)", color: "var(--burgundy)" }}>Become a member</Link>
              <Link to="/login" data-testid="cta-login-link"
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium border"
                style={{ borderColor: "var(--gold-soft)", color: "var(--bg-cream)" }}>Sign in to My Temple</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
